import { ZfDialog, ZfDialogContent, ZfDialogFooter, ZfDialogHeader, ZfStack, ZfTextButton, ZfTypography } from '@zafire/react';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IWealthNavigatorMicroFrontendProps } from '../interfaces/wealth-navigator';

interface IUsePresentationModeResult {
    isPresentationMode: boolean;
    setPresentationMode: (isPresentationModeOn: boolean) => void;
    presentationModeExitDialog: ReactNode;
}

export function usePresentationMode({ isWealthNavigatorMFE, dispatchPresentationModeEvent }: IWealthNavigatorMicroFrontendProps): IUsePresentationModeResult {
    const { t } = useTranslation();
    const [isPresentationMode, updatePresentationMode] = useState(false);
    const [showExitConfirmation, setShowExitConfirmation] = useState(false);

    const dispatchPresentationModeChange = useCallback(
        (isPresentationModeOn: boolean) => {
            dispatchPresentationModeEvent?.(isPresentationModeOn);
            updatePresentationMode(isPresentationModeOn);
        },
        [dispatchPresentationModeEvent],
    );

    const setPresentationMode = useCallback(
        (isModeOn: boolean) => {
            if (isWealthNavigatorMFE) {
                if (isModeOn) dispatchPresentationModeChange(isModeOn);
                else setShowExitConfirmation(true);
            } else {
                updatePresentationMode(isModeOn);
            }
        },
        [isWealthNavigatorMFE, dispatchPresentationModeChange],
    );

    const onPresentationModeOffConfirm = useCallback(() => {
        dispatchPresentationModeChange(false);
        setShowExitConfirmation(false);
    }, [dispatchPresentationModeChange]);

    const presentationModeExitDialog = useMemo(
        () => (
            <ZfDialog size="s" show={showExitConfirmation} onDialogClose={() => setShowExitConfirmation(false)}>
                <ZfDialogHeader>{t('presentationModeConfirmDialogTitle')}</ZfDialogHeader>
                <ZfDialogContent>
                    <ZfTypography>{t('presentationModeConfirmDialogTurnOffText')}</ZfTypography>
                </ZfDialogContent>
                <ZfDialogFooter>
                    <ZfStack slot="primary" direction="row" spacing="small">
                        <ZfTextButton size="medium" hierarchy="tertiary" onClick={() => setShowExitConfirmation(false)}>
                            {t('presentationModeConfirmDialogCancelButton')}
                        </ZfTextButton>
                        <ZfTextButton size="medium" kind="destructive" onClick={() => onPresentationModeOffConfirm()}>
                            {t('presentationModeConfirmDialogConfirmButton')}
                        </ZfTextButton>
                    </ZfStack>
                </ZfDialogFooter>
            </ZfDialog>
        ),
        [onPresentationModeOffConfirm, showExitConfirmation, t],
    );

    return { isPresentationMode, setPresentationMode, presentationModeExitDialog };
}
